'use client';

import { Button } from '@koda/koda-components/atoms/Button';
import ContentDialog from '@koda/koda-components/molecules/ContentDialog';
import { DatePicker } from '@koda/koda-components/molecules/DatePicker';
import NumberField from '@koda/koda-components/molecules/NumberField';
import TextField from '@koda/koda-components/molecules/TextField';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { ReactElement, useState } from 'react';
import { INITIAL_INFORMATION_ROUTE } from '../../../../../../i18n/pathnames/licenses';
import { LicenseTypes } from '../../types/licenses';
import { Past5Years } from '../business-information/forms/consts/dateIntervals';
import FormHeadline from '../business-information/forms/shared/FormHeadline';
import FormLayout from '../business-information/forms/shared/FormLayout';
import { useLicenseBusiness } from '../business-information/hooks/useLicenseBusiness';
import {
  createTheaterInitialInformationDefaults,
  THEATER_CONTENT_PATH,
  theaterInitialInformationSchema,
} from './theater.Schema';

export const TheaterInitial = (): ReactElement => {
  const licenseCode = LicenseTypes.Theater;
  const t = useTranslations(THEATER_CONTENT_PATH);
  const { values, setField, getError, showErrors } = useLicenseBusiness(licenseCode, {
    schema: theaterInitialInformationSchema,
    defaults: createTheaterInitialInformationDefaults,
    informationSlug: INITIAL_INFORMATION_ROUTE,
  });
  const [openGuidelines, setOpenGuidelines] = useState(false);
  const today = new Date();

  return (
    <FormLayout>
      <div className="flex flex-col gap-2 rounded-lg bg-lightblue-100 p-6 font-graphik">
        <p className="text-lg font-medium">{t('attentionBox.title')}</p>
        <p>
          {t.rich('attentionBox.body', {
            musicGuidelinesLink: chunks => (
              <Button
                onClick={e => {
                  e.preventDefault();
                  setOpenGuidelines(p => !p);
                }}
                buttonType="link"
                href="#"
                variant="inlineLink"
              >
                {chunks}
              </Button>
            ),
          })}
        </p>
      </div>
      <FormHeadline title={t('showInfoSection.title')} />
      <TextField
        label={t('showInfoSection.theaterName.label')}
        name="theaterName"
        placeholder={t('showInfoSection.theaterName.placeholder')}
        styleVariant="border"
        type="text"
        value={values?.theaterName ?? ''}
        onChange={e => setField('theaterName', e.target.value)}
        error={showErrors ? getError('theaterName') : undefined}
      />
      <TextField
        label={t('showInfoSection.performanceTitle.label')}
        name="performanceTitle"
        styleVariant="border"
        type="text"
        value={values?.performanceTitle ?? ''}
        onChange={e => setField('performanceTitle', e.target.value)}
        error={showErrors ? getError('performanceTitle') : undefined}
      />
      <TextField
        label={t('showInfoSection.performanceDuration.label')}
        name="performanceDuration"
        placeholder={t('showInfoSection.performanceDuration.placeholder')}
        styleVariant="border"
        type="text"
        value={values?.performanceDuration ?? ''}
        onChange={e => setField('performanceDuration', e.target.value)}
        error={showErrors ? getError('performanceDuration') : undefined}
      />
      <NumberField
        label={t('showInfoSection.totalShowings.label')}
        name="totalShowings"
        formatThousands
        value={values?.totalShowings}
        onChange={e => setField('totalShowings', parseInt(e.target.value))}
        error={values?.totalShowings || showErrors ? getError('totalShowings') : undefined}
        min={1}
        sign="positive-only"
        styleVariant="border"
      />
      <DatePicker
        selectionMode="both"
        value={(() => {
          const raw = values.performanceDateOrPeriod;
          if (!raw || raw.includes(' - ')) return undefined;
          return dayjs(raw, 'YYYY-MM-DD', true).isValid() ? new Date(raw) : undefined;
        })()}
        rangeValue={(() => {
          const raw = values.performanceDateOrPeriod;
          if (!raw) return undefined;
          if (raw.includes(' - ')) {
            const [fromRawStr, toRawStr] = raw.split(' - ').map(s => s.trim());
            const fromValid = dayjs(fromRawStr, 'YYYY-MM-DD', true).isValid();
            const toValid = dayjs(toRawStr, 'YYYY-MM-DD', true).isValid();
            if (!fromValid) return undefined;
            return {
              from: new Date(fromRawStr as string),
              to: toValid ? new Date(toRawStr as string) : undefined,
            };
          }
          if (dayjs(raw, 'YYYY-MM-DD', true).isValid()) {
            return { from: new Date(raw), to: undefined };
          }
          return undefined;
        })()}
        label={t('showInfoSection.performanceDateOrPeriod.label')}
        name={t('showInfoSection.performanceDateOrPeriod.label')}
        placeholder={t('showInfoSection.performanceDateOrPeriod.placeholder')}
        initialVisibleValue={new Date(today.getFullYear(), today.getMonth() - 1, 1)}
        interval={Past5Years}
        onChange={date => {
          setField('performanceDateOrPeriod', date ? dayjs(date).format('YYYY-MM-DD') : '');
        }}
        onRangeChange={range => {
          if (!range) {
            setField('performanceDateOrPeriod', '');
            return;
          }
          const fromStr = range.from ? dayjs(range.from).format('YYYY-MM-DD') : '';
          const toStr = range.to ? dayjs(range.to).format('YYYY-MM-DD') : '';
          if (range.from && range.to && dayjs(range.from).isSame(range.to, 'day')) {
            setField('performanceDateOrPeriod', fromStr);
            return;
          }
          setField('performanceDateOrPeriod', fromStr && toStr ? `${fromStr} - ${toStr}` : fromStr);
        }}
        validated={!!values.performanceDateOrPeriod && !getError('performanceDateOrPeriod')}
        error={values.performanceDateOrPeriod || showErrors ? getError('performanceDateOrPeriod') : undefined}
      />
      <div className="grid grid-cols-2 gap-4">
        <NumberField
          label={t('showInfoSection.totalAudience.label')}
          name="totalAudience"
          placeholder={t('showInfoSection.totalAudience.placeholder')}
          styleVariant="border"
          formatThousands
          value={values?.totalAudience}
          onChange={e => setField('totalAudience', parseInt(e.target.value))}
          error={values?.totalAudience || showErrors ? getError('totalAudience') : undefined}
        />
        <NumberField
          label={t('showInfoSection.totalTicketRevenue.label')}
          name="totalTicketRevenue"
          placeholder={t('showInfoSection.totalTicketRevenue.placeholder')}
          styleVariant="border"
          formatThousands
          value={values?.totalTicketRevenue ?? ''}
          onChange={e => setField('totalTicketRevenue', parseInt(e.target.value))}
          error={showErrors ? getError('totalTicketRevenue') : undefined}
        />
      </div>
      <ContentDialog
        open={openGuidelines}
        onOpenChange={setOpenGuidelines}
        heading={t('guidelines.title')}
        content={
          <div className="space-y-8 font-graphik text-md leading-default">
            <p>{t('guidelines.rubric')}</p>
            <section className="space-y-3">
              <h3 className="font-semibold">{t('guidelines.bodyHeader')}</h3>
              <p>{t('guidelines.body.listIntroduction')}</p>
              <ul className="list-disc space-y-1 pl-5">
                {[
                  { text: t('guidelines.body.listItems.item1') },
                  { text: t('guidelines.body.listItems.item2') },
                  { text: t('guidelines.body.listItems.item3') },
                  { text: t('guidelines.body.listItems.item4') },
                ].map((item, index) => (
                  <li key={index}>{item.text}</li>
                ))}
              </ul>
              <p>{t('guidelines.body.conclusion')}</p>
            </section>
            <section className="space-y-2">
              <h3 className="font-semibold">{t('guidelines.body.sections.backgroundMusic.title')}</h3>
              <p>{t('guidelines.body.sections.backgroundMusic.body')}</p>
            </section>
            <section className="space-y-2">
              <h3 className="font-semibold">{t('guidelines.body.sections.carbarets.title')}</h3>
              <p>{t('guidelines.body.sections.carbarets.body')}</p>
            </section>
            <section className="space-y-2">
              <h3 className="font-semibold">{t('guidelines.body.sections.revue.title')}</h3>
              <p>
                {t.rich('guidelines.body.sections.revue.body', {
                  link: chunks => (
                    <Button
                      href={t('guidelines.body.sections.revue.link')}
                      variant={'link'}
                      target="_blank"
                      buttonType={'link'}
                    >
                      {chunks}
                    </Button>
                  ),
                })}
              </p>
            </section>
            <section className="space-y-2">
              <h3 className="font-semibold">{t('guidelines.body.sections.sizeOfRights.title')}</h3>
              <p>{t('guidelines.body.sections.sizeOfRights.body')}</p>
              <p>
                {t.rich('guidelines.body.sections.sizeOfRights.linkText', {
                  link: chunks => (
                    <Button
                      href={t('guidelines.body.sections.sizeOfRights.link')}
                      variant={'link'}
                      target="_blank"
                      buttonType={'link'}
                    >
                      {chunks}
                    </Button>
                  ),
                })}
              </p>
            </section>
            <section className="space-y-3">
              <h3 className="font-semibold">{t('guidelines.body.applicationSection.title')}</h3>
              <p>
                {t.rich('guidelines.body.applicationSection.body', {
                  email: chunks => (
                    <Button
                      variant="inlineLink"
                      target="_blank"
                      buttonType="link"
                      href={`mailto:${t('guidelines.body.applicationSection.email')}`}
                    >
                      {chunks}
                    </Button>
                  ),
                })}
              </p>
              <ul className="list-disc space-y-1 pl-5">
                {Array.from(Array(9).keys()).map(i => (
                  <li key={i}>{t('guidelines.body.applicationSection.listItems.item' + (i + 1))}</li>
                ))}
              </ul>
            </section>
          </div>
        }
      />
    </FormLayout>
  );
};
